import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { App } from 'antd'
import {
  createValidationRecord,
  type ReviewedBatch,
  type ValidationMethod,
} from '../../features/validation'
import type { MindMapNode } from '@bandos/shared'

interface UseCreateValidationNodeArgs {
  mindmapId: string | undefined
  method: ValidationMethod | null
  nodes: MindMapNode[] | undefined
  selectionIds: Set<string> | null
}

/**
 * Encapsulates the "Create Survey/Interview" flow:
 * persist the batch as a standalone survey/interview row, then navigate to
 * the Research page for this mindmap. No mindmap save round-trip needed —
 * surveys and interviews are no longer mindmap nodes.
 */
export function useCreateValidationNode({
  mindmapId,
  method,
  nodes,
  selectionIds,
}: UseCreateValidationNodeArgs) {
  const navigate = useNavigate()
  const { notification } = App.useApp()

  return useCallback(
    async (batch: ReviewedBatch) => {
      if (!mindmapId || !method || !nodes) return

      try {
        const result = await createValidationRecord(
          batch,
          mindmapId,
          nodes,
          selectionIds ? [...selectionIds] : []
        )

        if (result.error) {
          notification.error({
            message: 'Failed to create validation',
            description: result.error.message,
            title: undefined,
          })
          return
        }

        void navigate(`/mindmap/${mindmapId}/research`)
      } catch (err) {
        notification.error({
          message: 'Failed to create validation',
          description: err instanceof Error ? err.message : 'Something went wrong.',
          title: undefined,
        })
      }
    },
    [mindmapId, method, nodes, selectionIds, navigate, notification]
  )
}
